/**
 * The floating upload panel (Import/Export Flow V2).
 *
 * One component for both flows the design draws identically — a
 * bottom-right card, one row per file, minimize to a pill, and a CTA
 * that hands control back to the user. Only the wording and that CTA
 * differ:
 *
 *   • **create** (Submission Triage) — titled "TRI-89 - New Submission",
 *     the id arriving with the POST rather than at the end. CTA is
 *     **View Submission**: the worker already built the submission, so
 *     this only navigates there.
 *   • **attach** (a submission's Files tab) — titled "Extracting Data",
 *     CTA **Update Submission**, which lets the layout refresh on the
 *     user's word instead of reloading the page underneath them.
 *
 * Mounted globally so a batch survives navigation. Closing mid-flight
 * asks first: the queued jobs keep running server-side, but the UI stops
 * tracking them.
 */
import { useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router";
import { CheckCircle, ChevronDown, ChevronUp, Loading02, Stars02, Trash02, XCircle, XClose } from "@untitledui/icons";
import { Button, cx } from "@tricura/atlas-ui";
import { WarningDialog } from "./warning-dialog";
import {
    applyBatch,
    cancelBatch,
    dismissUploadStatus,
    getUploadStatusSnapshot,
    removeBatchFile,
    retryBatchFile,
    subscribeUploadStatus,
} from "../state/upload-status";
import { formatSubmissionId } from "../utils/submission-id";

const FORMATS_LINE = "Supported formats: PDF, PNG, JPG, JPEG, XLS, XLSX · Max 50 MB per file";

/** "20 KB"-style size label. */
function sizeLabel(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** The worker reports states, not bytes, so each state maps to the
 *  Figma's representative fill rather than a real percentage. */
function progressFor(status: string): number {
    if (status === "uploading") return 30;
    if (status === "extracting") return 60;
    return 100;
}

export function ExtractionPanel() {
    const state = useSyncExternalStore(subscribeUploadStatus, getUploadStatusSnapshot, getUploadStatusSnapshot);
    const [minimized, setMinimized] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const navigate = useNavigate();

    if (state.phase !== "batch") return null;

    const isCreate = state.mode === "create";
    // The Triage pricing import also ENDS in a new submission, so its CTA
    // navigates like create's — but the id only exists once the job
    // completes, so the title keeps the attach wording meanwhile.
    const viewsNewSubmission = isCreate || (state.mode === "pricing" && state.pricingCreates === true);
    const failed = state.files.some((f) => f.status === "failed");
    const anyComplete = state.files.some((f) => f.status === "complete");

    // Create names itself after the submission being built; attach
    // describes the operation, since the submission is already on screen.
    const title = isCreate
        ? `${state.submissionId ? formatSubmissionId(state.submissionId) : "New"} - New Submission`
        : !state.settled
          ? "Extracting Data"
          : failed && !anyComplete
            ? "Extraction Failed"
            : "Extraction Complete";

    const onClose = () => {
        if (!state.settled) {
            setConfirmCancel(true);
            return;
        }
        dismissUploadStatus();
    };

    const headerIcon = !state.settled ? (
        <Loading02 aria-hidden="true" className="text-brand-secondary size-5 shrink-0 animate-spin" />
    ) : failed && !anyComplete ? (
        <XCircle aria-hidden="true" className="text-error-primary size-5 shrink-0" />
    ) : (
        <CheckCircle aria-hidden="true" className="text-success-primary size-5 shrink-0" />
    );

    return (
        <>
            <section
                aria-label={title}
                data-autotest="files-extraction-panel"
                // Figma: 516px expanded, 508x68 minimized; radius-2xl.
                className={cx(
                    "border-secondary bg-primary fixed bottom-6 right-6 z-40 max-w-[calc(100vw-3rem)] rounded-2xl border shadow-lg",
                    minimized ? "flex h-[68px] w-[508px] items-center" : "w-[516px]",
                )}
            >
                <header
                    className={cx("flex justify-between gap-3 px-4", minimized ? "w-full items-center" : "items-start pb-1 pt-3")}
                >
                    <div className="flex flex-col gap-0.5">
                        <span className="text-primary inline-flex items-center gap-2 text-sm font-semibold">
                            {minimized && headerIcon}
                            {title}
                        </span>
                        {!minimized && <span className="text-tertiary text-xs">{FORMATS_LINE}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            aria-label={minimized ? "Expand" : "Minimize"}
                            onClick={() => setMinimized((m) => !m)}
                            data-autotest="files-extraction-panel-toggle-button"
                            className="text-fg-quaternary hover:text-fg-secondary cursor-pointer rounded p-1"
                        >
                            {minimized ? (
                                <ChevronUp aria-hidden="true" className="size-4" />
                            ) : (
                                <ChevronDown aria-hidden="true" className="size-4" />
                            )}
                        </button>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={onClose}
                            data-autotest="files-extraction-panel-close-button"
                            className="text-fg-quaternary hover:text-fg-secondary cursor-pointer rounded p-1"
                        >
                            <XClose aria-hidden="true" className="size-4" />
                        </button>
                    </div>
                </header>

                {!minimized && (
                    /* Capped so a long batch scrolls inside the panel rather
                     * than growing past the viewport (Figma: "allow scrolling
                     * vertically"). */
                    <div className="atlas-scrollbar flex max-h-[22rem] flex-col gap-4 overflow-y-auto px-4 pb-4 pt-2">
                        {state.files.map((f) => (
                            <div
                                key={f.id}
                                data-autotest="files-extraction-panel-file-uploaded"
                                className={cx(
                                    "rounded-lg px-3 py-2.5",
                                    f.status === "failed" ? "border-error border-2" : "border-secondary border",
                                )}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-primary truncate text-sm font-medium">{f.name}</span>
                                    <button
                                        type="button"
                                        aria-label={`Remove ${f.name}`}
                                        onClick={() => removeBatchFile(f.id)}
                                        data-autotest="files-extraction-panel-remove-file-button"
                                        className="text-fg-quaternary hover:text-fg-secondary -m-1 shrink-0 cursor-pointer rounded p-1"
                                    >
                                        <Trash02 aria-hidden="true" className="size-4" />
                                    </button>
                                </div>
                                <div className="text-tertiary mt-0.5 flex items-center gap-1.5 text-xs">
                                    <span>{sizeLabel(f.size)}</span>
                                    <span aria-hidden="true" className="text-fg-quaternary">
                                        |
                                    </span>
                                    {f.status === "uploading" && <span>Uploading…</span>}
                                    {f.status === "extracting" && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Stars02 aria-hidden="true" className="text-fg-quaternary size-4 shrink-0" />
                                            Extracting data…
                                        </span>
                                    )}
                                    {f.status === "complete" && (
                                        <span className="text-success-primary inline-flex items-center gap-1 font-semibold">
                                            <CheckCircle aria-hidden="true" className="size-4 stroke-[2.5px]" /> Complete
                                        </span>
                                    )}
                                    {f.status === "failed" && (
                                        <span className="text-error-primary inline-flex items-center gap-1 font-semibold">
                                            <XCircle aria-hidden="true" className="size-4 stroke-[2.5px]" /> Failed
                                        </span>
                                    )}
                                </div>
                                {f.status !== "failed" ? (
                                    <div className="mt-2 flex items-center gap-2">
                                        <div className="bg-secondary h-1.5 grow overflow-hidden rounded-full">
                                            <div
                                                className="bg-brand-solid h-full rounded-full transition-all"
                                                style={{ width: `${progressFor(f.status)}%` }}
                                            />
                                        </div>
                                        <span className="text-tertiary text-xs tabular-nums">{progressFor(f.status)}%</span>
                                    </div>
                                ) : (
                                    <>
                                        {/* The backend's reason — for the pricing model this
                                          * is the per-cell problem list, which the user needs
                                          * to fix the workbook. Clamped: it can run long. */}
                                        {f.error && (
                                            <p className="text-error-primary mt-2 line-clamp-3 text-xs" title={f.error}>
                                                {f.error}
                                            </p>
                                        )}
                                        {/* Create sends the whole batch in one request, so a
                                          * failed row there is removed rather than retried. */}
                                        {!isCreate && (
                                            <button
                                                type="button"
                                                onClick={() => retryBatchFile(f.id)}
                                                className="text-error-primary mt-2 block cursor-pointer whitespace-nowrap text-sm font-semibold hover:underline"
                                            >
                                                Try again
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}

                        <div className="flex justify-end">
                            {viewsNewSubmission ? (
                                <Button
                                    size="sm"
                                    isDisabled={!state.settled || !state.submissionId}
                                    data-autotest="files-extraction-panel-view-submission-button"
                                    onClick={() => {
                                        const id = state.submissionId;
                                        dismissUploadStatus();
                                        if (id) navigate(`/submissions/${id}/uw-information`);
                                    }}
                                >
                                    View Submission
                                </Button>
                            ) : (
                                <Button
                                    size="sm"
                                    isDisabled={!state.settled || !anyComplete}
                                    onClick={() => applyBatch()}
                                    data-autotest="files-extraction-panel-update-submission-button"
                                >
                                    Update Submission
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </section>

            <WarningDialog
                isOpen={confirmCancel}
                onClose={() => setConfirmCancel(false)}
                title="Cancel Upload?"
                description="Your upload is not complete yet."
                data-autotest="files-extraction-panel-cancel-confirm-dialog"
                actions={
                    <>
                        <Button
                            color="secondary"
                            size="md"
                            onClick={() => {
                                setConfirmCancel(false);
                                cancelBatch();
                            }}
                        >
                            Cancel Upload
                        </Button>
                        <Button size="md" onClick={() => setConfirmCancel(false)}>
                            Continue Upload
                        </Button>
                    </>
                }
            />
        </>
    );
}
